/**
 * 站点结构与页面清单的唯一来源（对齐 docs/05 §4.1）。
 * 菜单、路由、面包屑都从这里生成；新增页面只改这里 + 一个模板组件。
 *
 * `real` 标记后端端点是否已实现：未实现的模块统一用示例数据渲染，页面顶部显式提示，避免把假数据当成品。
 */

export type PageTemplate =
  | 'dashboard' // 驾驶舱
  | 'list' // 标准单据列表（查询 + 分页 + 导出）
  | 'detail' // 标准单据详情
  | 'report' // 报表（多维汇总 + 导出）
  | 'form' // 单据录入（当前仅申领已实现）
  | 'pending'; // 仅有占位骨架

export interface ModulePage {
  /** 路由相对路径，如 `/stock/onhand`。 */
  path: string;
  title: string;
  template: PageTemplate;
  /** 单号前缀（示例数据用）：RK / CK / PD / PS / QS / TH / JS … */
  docPrefix?: string;
  /** 是否已有后端端点。 */
  real?: boolean;
  /** 是否生成 `${path}/:id` 详情路由。 */
  detail?: boolean;
  /** 补充说明（页面顶部展示）。 */
  note?: string;
}

export interface ModuleGroup {
  key: string;
  title: string;
  pages: ModulePage[];
}

export const modules: ModuleGroup[] = [
  {
    key: 'home',
    title: '首页',
    pages: [{ path: '/workbench', title: '工作台', template: 'dashboard' }]
  },
  {
    key: 'apply',
    title: '申领',
    pages: [
      { path: '/apply', title: '申领列表', template: 'list', docPrefix: 'SQ', real: true, detail: true },
      { path: '/apply/create', title: '新建申领', template: 'form', real: true },
      { path: '/apply/favorite', title: '常购清单', template: 'list', docPrefix: 'SQ', real: true }
    ]
  },
  {
    key: 'approval',
    title: '审批',
    pages: [
      { path: '/approval/todo', title: '待我审批', template: 'list', docPrefix: 'SQ', real: true },
      { path: '/approval/done', title: '我已审批', template: 'list', docPrefix: 'SQ', real: true }
    ]
  },
  {
    key: 'delivery',
    title: '配送与签收',
    pages: [
      { path: '/delivery', title: '配送单', template: 'list', docPrefix: 'PS', detail: true, real: true },
      { path: '/delivery/track', title: '轨迹跟踪', template: 'list', docPrefix: 'PS', real: true },
      { path: '/sign', title: '签收记录', template: 'list', docPrefix: 'QS', detail: true, real: true },
      { path: '/sign/scan', title: '扫码签收', template: 'list', docPrefix: 'PS', real: true, note: '扫码走 /api/wl/scan/resolve 归一码值。' }
    ]
  },
  {
    key: 'stock',
    title: '库存',
    pages: [
      // 现存量与批次/效期同屏（StockList 已含批次、效期、可占用列），不再单列 /stock/batch
      { path: '/stock', title: '现存量', template: 'list', docPrefix: 'KC', real: true },
      { path: '/stock/ledger', title: '库存流水', template: 'list', docPrefix: 'LS', detail: true, real: true },
      { path: '/inventory/alert', title: '库存预警', template: 'list', docPrefix: 'AL', real: true }
    ]
  },
  {
    key: 'io',
    title: '出入库',
    pages: [
      { path: '/inventory/inbound', title: '入库单', template: 'list', docPrefix: 'RK', detail: true, real: true },
      { path: '/inventory/outbound', title: '出库单', template: 'list', docPrefix: 'CK', detail: true, real: true }
    ]
  },
  {
    key: 'stocktake',
    title: '盘点',
    pages: [{ path: '/stocktake', title: '盘点计划', template: 'list', docPrefix: 'PD', detail: true, real: true }]
  },
  {
    key: 'substock',
    title: '二级库 / SPD',
    pages: [
      { path: '/substock', title: '科室小库', template: 'list', docPrefix: 'EK', real: true },
      { path: '/substock/consume', title: '消耗确认', template: 'list', docPrefix: 'CB', real: true },
      { path: '/cost', title: '成本转移', template: 'list', docPrefix: 'CB', detail: true, real: true }
    ]
  },
  {
    key: 'return',
    title: '退换货',
    pages: [{ path: '/return', title: '退换货申请', template: 'list', docPrefix: 'TH', detail: true, real: true }]
  },
  {
    key: 'purchase',
    title: '请购采购',
    pages: [
      { path: '/require', title: '请购单', template: 'list', docPrefix: 'QG', detail: true, real: true },
      { path: '/purchase', title: '采购订单', template: 'list', docPrefix: 'CG', detail: true, real: true }
    ]
  },
  {
    key: 'service',
    title: '报修售后',
    pages: [{ path: '/workorder', title: '报修工单', template: 'list', docPrefix: 'WX', real: true }]
  },
  {
    key: 'settle',
    title: '对账结算',
    pages: [
      { path: '/settle', title: '对账单', template: 'list', docPrefix: 'JS', detail: true, real: true },
      { path: '/settle/invoice', title: '发票与付款', template: 'list', docPrefix: 'FP', real: true }
    ]
  },
  {
    key: 'budget',
    title: '预算',
    pages: [
      { path: '/budget/plan', title: '预算编制', template: 'list', docPrefix: 'YS', detail: true, real: true },
      // 执行页路由为 /budget（与菜单一致），此前写 /budget/usage 会落 404
      { path: '/budget', title: '预算执行', template: 'list', docPrefix: 'YS', real: true }
    ]
  },
  {
    key: 'analysis',
    title: '分析与监管',
    pages: [
      { path: '/reports', title: '报表中心', template: 'report', real: true },
      { path: '/dashboard', title: '驾驶舱', template: 'dashboard', real: true }
    ]
  },
  {
    key: 'basic',
    title: '基础资料',
    pages: [
      { path: '/master/material', title: '物资档案', template: 'list', docPrefix: 'WL', real: true },
      { path: '/basic/dept', title: '科室与空间', template: 'list', docPrefix: 'KS', real: true },
      { path: '/basic/warehouse', title: '仓库货位', template: 'list', docPrefix: 'WH', real: true },
      { path: '/basic/supplier', title: '供应商', template: 'list', docPrefix: 'GY', real: true },
      { path: '/basic/user', title: '人员与角色', template: 'list', docPrefix: 'YH', real: true }
    ]
  },
  {
    key: 'system',
    title: '系统管理',
    pages: [
      { path: '/system/flow', title: '审批流配置', template: 'list', docPrefix: 'LC', real: true },
      { path: '/system/budget', title: '预算与定额', template: 'list', docPrefix: 'DE', real: true },
      { path: '/system/dict', title: '数据字典', template: 'list', docPrefix: 'ZD', real: true },
      { path: '/system/audit', title: '审计日志', template: 'list', docPrefix: 'SJ', real: true }
    ]
  }
];

export const allPages: ModulePage[] = modules.flatMap((group) => group.pages);

export function findPage(path: string): ModulePage | undefined {
  return allPages.find((page) => page.path === path);
}

/** 面包屑：分组名 + 页面名。 */
export function breadcrumbOf(path: string): string[] {
  const group = modules.find((item) => item.pages.some((page) => page.path === path));
  const page = group?.pages.find((item) => item.path === path);
  return group && page ? [group.title, page.title] : [];
}
